import { useEffect, useState } from 'react';
import { fetchAicaFinancialMetrics, FinancialMetricsData, FinancialMetricsRow } from './financialMetricsApi';
import { computeDelta, DisplayMetric, formatMetricValue, KPI_METRIC_CONFIG, MetricTrend, SPARKLINE_METRIC_CONFIG } from './constants';

interface UseFinancialMetricsResult {
    loading: boolean;
    fyLabel: string;
    kpiMetrics: DisplayMetric[];
    sparklineMetrics: DisplayMetric[];
}

const FLAT_TREND_THRESHOLD = 0.05;
const TRAILING_TWO_DIGITS = /(\d{2})$/;

export const extractEndYear = (name: string) => name.match(TRAILING_TWO_DIGITS)?.[1];

export const getYearHeaders = (data: FinancialMetricsData) => data.headers.filter((h) => h.type);

export const buildSparklineFromYears = (
    row: FinancialMetricsRow,
    yearHeaders: { key: string }[],
): { points: { value: number }[]; trend: MetricTrend; delta: string | null } => {
    const raw = yearHeaders.map((h) => row[h.key]).filter((v): v is number => typeof v === 'number');
    if (raw.length < 2) return { points: [{ value: 0.5 }, { value: 0.5 }], trend: 'flat', delta: null };
    const min = Math.min(...raw);
    const max = Math.max(...raw);
    const range = max - min;
    const first = raw[0];
    const last = raw[raw.length - 1];
    if (range === 0) return { points: raw.map(() => ({ value: 0.5 })), trend: 'flat', delta: '0.0%' };
    const trend: MetricTrend = Math.abs(last - first) / range < FLAT_TREND_THRESHOLD ? 'flat' : last > first ? 'up' : 'down';
    const { delta } = computeDelta(last, first);
    return { points: raw.map((v) => ({ value: (v - min) / range })), trend, delta };
};

const toDisplayMetric = (
    config: { metricName: string; label: string },
    data: FinancialMetricsData,
    currentKey: string,
    periodLabel: string,
    yearHeaders: { key: string }[],
): DisplayMetric | null => {
    const row = data.rows.find((r) => r.metricName === config.metricName);
    if (!row) return null;

    const current = row[currentKey] as number | null | undefined;
    const sparkline = buildSparklineFromYears(row, yearHeaders);

    return {
        key: config.metricName,
        label: config.label,
        value: formatMetricValue(current, row.unit),
        delta: sparkline.delta,
        trend: sparkline.trend,
        sparkline: sparkline.points,
        periodLabel,
    };
};

export function useFinancialMetrics(orgId?: string): UseFinancialMetricsResult {
    const [data, setData] = useState<FinancialMetricsData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!orgId) return;
        let active = true;
        setLoading(true);
        fetchAicaFinancialMetrics(orgId)
            .then((result) => active && setData(result))
            .catch(() => active && setData(null))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [orgId]);

    if (!data) {
        return { loading, fyLabel: '', kpiMetrics: [], sparklineMetrics: [] };
    }

    const yearHeaders = getYearHeaders(data);
    const firstHeader = yearHeaders[0];
    const lastHeader = yearHeaders[yearHeaders.length - 1];
    const currentKey = lastHeader?.key ?? '';

    const startYear = firstHeader ? extractEndYear(firstHeader.name) : null;
    const endYear = lastHeader ? extractEndYear(lastHeader.name) : null;
    const fyLabel = startYear && endYear && startYear !== endYear ? `FY'${startYear}-${endYear}` : lastHeader?.name ?? '';
    const periodLabel = fyLabel;

    const transform = (configs: { metricName: string; label: string }[]) =>
        configs
            .map((config) => toDisplayMetric(config, data, currentKey, periodLabel, yearHeaders))
            .filter((m): m is DisplayMetric => m !== null);

    return {
        loading,
        fyLabel,
        kpiMetrics: transform(KPI_METRIC_CONFIG),
        sparklineMetrics: transform(SPARKLINE_METRIC_CONFIG),
    };
}
